import React from "react";
import { useTranslation } from "react-i18next";
import { ChevronLeft, ChevronRight } from "lucide-react";
import type { Slot, PollInsights } from "../../lib/insights/types";
import { dayScores } from "../../lib/insights/dayScores";

interface DayPagerProps {
  slots: Slot[];
  insights: PollInsights;
  activeDay: string;
  onChange: (date: string) => void;
  topDay: string | null;
}

const parseIsoDate = (dateIso: string): Date | null => {
  if (!dateIso) return null;
  const [y, m, d] = dateIso.split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

const dayLetter = (dateIso: string, locale: string): string => {
  const dt = parseIsoDate(dateIso);
  if (!dt) return "";
  const letter = new Intl.DateTimeFormat(locale, { weekday: "narrow" }).format(
    dt,
  );
  return `${letter} ${dt.getDate()}`;
};

/**
 * Day pager mobile : rangée horizontale de chips scroll-snap + arrows ‹ ›.
 * Cf. Results Final.html lignes 442-486.
 */
const DayPager: React.FC<DayPagerProps> = ({
  slots,
  insights,
  activeDay,
  onChange,
  topDay,
}) => {
  const { t, i18n } = useTranslation("polls");
  const locale = i18n.language || "fr";
  const days = React.useMemo(
    () => dayScores(slots, insights),
    [slots, insights],
  );
  const totalVoters = insights.participantsCount;
  const activeIdx = days.findIndex((d) => d.date === activeDay);

  return (
    <div data-testid="day-pager" className="flex flex-col gap-2">
      <div
        data-testid="day-pager-chips"
        className="flex gap-1.5 overflow-x-auto px-3 pb-1"
        style={{ scrollSnapType: "x mandatory" }}
      >
        {days.map((d) => {
          const isActive = d.date === activeDay;
          const isTop = topDay === d.date;
          const hot = totalVoters > 0 && d.bestYes >= totalVoters * 0.7;
          return (
            <button
              type="button"
              key={d.date}
              data-testid={`day-pager-chip-${d.date}`}
              aria-pressed={isActive}
              onClick={() => onChange(d.date)}
              style={{ scrollSnapAlign: "center" }}
              className={`flex-shrink-0 min-w-[45px] sm:min-w-[70px] text-center rounded-xl border px-2.5 py-1.5 transition-colors cursor-pointer ${
                isActive
                  ? "border-primary bg-card text-foreground"
                  : isTop
                    ? "border-vote-available/50 bg-vote-available/10 text-foreground hover:bg-vote-available/15"
                    : "border-border bg-muted/40 text-muted-foreground hover:text-foreground"
              }`}
            >
              <div className="text-[9px] font-semibold uppercase tracking-wide flex items-center justify-center gap-0.5">
                {isTop && <span className="text-warning">★</span>}
                {dayLetter(d.date, locale)}
              </div>
              <div
                className={`text-[13px] font-bold tabular-nums mt-0.5 ${hot ? "text-vote-available-foreground" : "text-foreground"}`}
              >
                {d.bestYes}
                <span className="text-[9px] font-medium text-muted-foreground/70">
                  /{totalVoters}
                </span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-2 px-3">
        <button
          type="button"
          data-testid="day-pager-prev"
          disabled={activeIdx <= 0}
          onClick={() => activeIdx > 0 && onChange(days[activeIdx - 1].date)}
          className="h-7 w-7 rounded-full border border-border bg-muted/40 text-muted-foreground flex items-center justify-center disabled:opacity-40 hover:text-foreground transition-colors"
          aria-label={t("dayPager.prevDay")}
        >
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        </button>
        <div className="flex-1 flex items-center justify-center gap-1.5 text-[13px] font-semibold">
          {topDay === activeDay && <span className="text-warning">★</span>}
          {dayLabel(activeDay, locale)}
        </div>
        <button
          type="button"
          data-testid="day-pager-next"
          disabled={activeIdx < 0 || activeIdx >= days.length - 1}
          onClick={() =>
            activeIdx >= 0 &&
            activeIdx < days.length - 1 &&
            onChange(days[activeIdx + 1].date)
          }
          className="h-7 w-7 rounded-full border border-border bg-muted/40 text-muted-foreground flex items-center justify-center disabled:opacity-40 hover:text-foreground transition-colors"
          aria-label={t("dayPager.nextDay")}
        >
          <ChevronRight className="h-3.5 w-3.5" aria-hidden />
        </button>
      </div>
    </div>
  );
};

function dayLabel(dateIso: string, locale: string): string {
  const dt = parseIsoDate(dateIso);
  if (!dt) return "";
  const label = new Intl.DateTimeFormat(locale, {
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(dt);
  return label.charAt(0).toUpperCase() + label.slice(1);
}

export default DayPager;
